import { useEffect, useRef, useState } from 'react';
import api from '../api';
import { useProject } from '../contexts/ProjectContext';
import { useOrchestratorContext, clearOrchestratorContext } from './useOrchestratorContext';

export interface OrchestratorProjectGuard {
  expectedProjectId: number | null;
  mismatch: boolean;
  mismatchReason: string | null;
}

export function useOrchestratorProjectGuard(enabled: boolean = true): OrchestratorProjectGuard {
  const ctx = useOrchestratorContext();
  const { currentProject, projects, setCurrentProject, refreshProjects } = useProject();

  const expectedProjectId = ctx.inRouter && ctx.extraParams?.projectId
    ? parseInt(ctx.extraParams.projectId, 10)
    : NaN;

  const fetchAttempted = useRef<Set<number>>(new Set());
  // Persisted in sessionStorage so auto-sync only happens ONCE per session,
  // even across page navigations. Without this, navigating to Process Documentation
  // from the sidebar would re-trigger the auto-switch to the old session project.
  const GUARD_KEY = 'jarvis_orchestrator_guard_applied';
  const getApplied = () => sessionStorage.getItem(GUARD_KEY);
  const setApplied = (key: string) => sessionStorage.setItem(GUARD_KEY, key);
  // Suppress the mismatch banner until the initial auto-sync has completed.
  // This prevents a flash when the page loads with the wrong project and the
  // guard is in the middle of switching the dropdown to the session's project.
  const [syncSettled, setSyncSettled] = useState(false);

  useEffect(() => {
    if (!enabled || !ctx.inRouter || !Number.isInteger(expectedProjectId)) {
      setSyncSettled(true);
      return;
    }

    // Already on the correct project — settle immediately.
    // Also mark as applied so if user manually switches away later,
    // the guard won't force-switch back.
    if (currentProject && currentProject.id === expectedProjectId) {
      const applyKey = `${ctx.sessionId}:${expectedProjectId}`;
      if (getApplied() !== applyKey) setApplied(applyKey);
      setSyncSettled(true);
      return;
    }

    const applyKey = `${ctx.sessionId}:${expectedProjectId}`;

    // Guard already auto-synced once for this session (persisted across page
    // navigations via sessionStorage) — the user manually switched away after
    // that. Settle so the banner can show instead of force-switching again.
    if (getApplied() === applyKey) {
      setSyncSettled(true);
      return;
    }

    // Auto-sync is still in progress — keep banner suppressed.
    setSyncSettled(false);

    const target = projects.find((p) => p.id === expectedProjectId);
    if (target) {
      setApplied(applyKey);
      setCurrentProject(target);
      // syncSettled will flip to true on the next render when
      // currentProject.id === expectedProjectId.
      return;
    }

    if (fetchAttempted.current.has(expectedProjectId)) return;
    fetchAttempted.current.add(expectedProjectId);

    (async () => {
      try {
        const { data } = await api.get(`/api/projects/${expectedProjectId}`);
        const project = data?.project || data;
        if (project && project.id === expectedProjectId) {
          setApplied(applyKey);
          setCurrentProject(project);
          refreshProjects().catch(() => {});
        } else {
          setSyncSettled(true);
        }
      } catch {
        fetchAttempted.current.delete(expectedProjectId);
        refreshProjects().catch(() => {});
        setSyncSettled(true);
      }
    })();
  }, [enabled, ctx.inRouter, ctx.sessionId, expectedProjectId, projects, currentProject, setCurrentProject, refreshProjects]);

  // Only report mismatch once the initial auto-sync has settled.
  const mismatch = syncSettled
    && ctx.inRouter
    && Number.isInteger(expectedProjectId)
    && !!currentProject
    && currentProject.id !== expectedProjectId;

  return {
    expectedProjectId: Number.isInteger(expectedProjectId) ? expectedProjectId : null,
    mismatch,
    mismatchReason: mismatch
      ? `Smart Router expected project #${expectedProjectId} but you have #${currentProject?.id} selected.`
      : null,
  };
}
